'use server'

import { apiRequest, ApiResponse } from '../request'

export type PaymentAccountStatus = 'PENDING_KYC' | 'ACTIVE' | 'BLOCKED' | 'CLOSED'

export interface PaymentAccount {
    id: number
    paymentProvider: string | null
    paymentAccountId: string | null
    paymentWalletId: string | null
    paymentAccountStatus: PaymentAccountStatus | null
    paymentBalanceCache: number
    paymentBalanceSyncAt: string | null
    paymentLegalName: string | null
    paymentCpfCnpj: string | null
    paymentBirthDate: string | null
    paymentIncomeValue: number | null
    // Status granular do onboarding/KYC por trilha (raw do provider:
    // NOT_SENT | PENDING | AWAITING_APPROVAL | APPROVED | REJECTED)
    paymentDocStatus: string | null
    paymentCommercialStatus: string | null
    paymentBankInfoStatus: string | null
    paymentOnboardingSyncAt: string | null
    allowSelfApproval: boolean
}

export interface AccountDocumentFile {
    id: string
    status: string | null
    type: string | null
}

export interface AccountDocumentGroup {
    id: string
    status: string
    type: string | null
    title: string | null
    description: string | null
    onboardingUrl: string | null
    responsibleName: string | null
    documentTypes: string[]
    files: AccountDocumentFile[]
}

export interface AccountDocuments {
    rejectReasons: string | null
    groups: AccountDocumentGroup[]
}

export interface ActivatePaymentAccountData {
    legalName: string
    cpfCnpj: string
    birthDate?: string
    mobilePhone: string
    email: string
    companyType: 'MEI' | 'LIMITED' | 'INDIVIDUAL' | 'ASSOCIATION'
    phone?: string
    address: string
    province: string
    postalCode: string
    complement?: string
    addressNumber: number
    incomeValue: number
    site?: string
}

const BASE = 'financial/payment-account'

export async function getPaymentAccount(): Promise<ApiResponse<PaymentAccount>> {
    return apiRequest<PaymentAccount>(BASE, {})
}

export async function activatePaymentAccount(
    data: ActivatePaymentAccountData,
): Promise<ApiResponse<PaymentAccount>> {
    return apiRequest<PaymentAccount>(`${BASE}/activate`, { data }, 'post')
}

export async function syncPaymentAccountStatus(): Promise<ApiResponse<{ status: PaymentAccountStatus; changed: boolean }>> {
    return apiRequest(`${BASE}/sync-status`, {}, 'post')
}

export async function syncPaymentAccountBalance(): Promise<ApiResponse<{ balanceCache: number }>> {
    return apiRequest(`${BASE}/sync-balance`, {}, 'post')
}

/**
 * Taxas que o provedor cobra NESTA conta. É o CUSTO da empresa — não confundir com
 * a taxa repassada ao cliente (`billing_fee_policy`), que é configurada em cima desta.
 * Todo valor em centavos; percentuais como número (2.69 = 2,69%).
 */
export interface ProviderAccountFees {
    payment: {
        bankSlip: { valueCents: number; daysToReceive: number | null }
        creditCard: {
            operationValueCents: number
            oneInstallmentPercent: number
            upToSixInstallmentsPercent: number
            upToTwelveInstallmentsPercent: number
            upToTwentyOneInstallmentsPercent: number
            daysToReceive: number | null
        }
        debitCard: {
            operationValueCents: number
            percent: number
            daysToReceive: number | null
        }
        pix: {
            fixedFeeCents: number
            percent: number
            minimumFeeCents: number
            maximumFeeCents: number
            monthlyCreditsWithoutFee: number
            creditsReceivedOfCurrentMonth: number
        }
    }
    transfer: {
        monthlyTransfersWithoutFee: number
        ted: { feeCents: number; countsTowardFreeQuota: boolean }
        pix: { feeCents: number; countsTowardFreeQuota: boolean }
    }
    anticipation: {
        creditCardDetachedMonthlyPercent: number
        creditCardInstallmentMonthlyPercent: number
        bankSlipMonthlyPercent: number
    }
    /** Quando a leitura foi feita — taxa sem data é indistinguível de taxa velha. */
    fetchedAt: string
}

export async function getProviderAccountFees(
    force = false,
): Promise<ApiResponse<ProviderAccountFees>> {
    return apiRequest<ProviderAccountFees>(`${BASE}/fees`, {
        params: force ? { force: 'true' } : {},
    })
}

export async function setAllowSelfApproval(allow: boolean): Promise<ApiResponse<PaymentAccount>> {
    return apiRequest(`${BASE}/self-approval`, { data: { allow } }, 'post')
}

// ============ DOCUMENTOS / KYC ============

export async function getAccountDocuments(): Promise<ApiResponse<AccountDocuments>> {
    return apiRequest<AccountDocuments>(`${BASE}/documents`, {})
}

export async function sendAccountDocument(
    documentId: string,
    type: string,
    file: File,
): Promise<ApiResponse<{ id: string }>> {
    const formData = new FormData()
    formData.append('type', type)
    formData.append('file', file)
    return apiRequest<{ id: string }>(
        `${BASE}/documents/${documentId}`,
        { data: formData, formData: true },
        'post',
    )
}

export async function deleteAccountDocumentFile(
    fileId: string,
): Promise<ApiResponse<{ deleted: boolean }>> {
    return apiRequest(`${BASE}/documents/files/${fileId}`, {}, 'delete')
}
